/**
 * §11 «Категория меню» (design-guest v7, route /menu/c/{category}, guest).
 *
 * Row-card каждой позиции: thumbnail 60×60 (фото позиции) + название +
 * состав (description) + chip аллергенов (красный при match с профилем) +
 * ЦЕНА в IBM Plex Mono справа + «+ В заказ» → stepper «− N +». Недоступные
 * (без активных позиций) свёрнуты снизу. Sticky-CTA «Перейти к заказу» внизу.
 * Назад ‹ → к плиткам категорий (§10).
 */
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Skeleton } from '@hsc/ui';
import {
  catalogItemCurrency,
  catalogItemPriceMinor,
  displayVariantPrice,
  formatPriceMinor,
  type CatalogViewCategory,
  type CatalogViewItem,
} from '@hsc/types';
import { resolveGuestLocale } from '../../lib/locale.js';
import { useCart } from './useCart.js';
import { useFnbCatalog } from './useMenu.js';
import { QtyStepper, allergenLabel, isAllergenWarn } from './guest-shared.js';
import { GuestCartCta } from './GuestCartCta.js';

/** Цена позиции (фиксированная) для строки меню; null — цену не показываем. */
function itemUnitPrice(item: CatalogViewItem, locale: string): string | null {
  const minor = catalogItemPriceMinor(item);
  const currency = catalogItemCurrency(item);
  if (!item.show_price || minor === undefined || currency === undefined) {
    return null;
  }
  return formatPriceMinor(minor, currency, locale);
}

/** Аллергены-чипы позиции (красные при match с профилем гостя). */
function AllergenChips({
  item,
  guestAllergens,
}: {
  item: CatalogViewItem;
  guestAllergens: readonly string[];
}): JSX.Element | null {
  const tags = item.allergens ?? [];
  if (tags.length === 0) return null;
  return (
    <span className="fnb-row__allergens" data-testid={`menu-item-${item.code}-allergens`}>
      {tags.map((t) => {
        const warn = isAllergenWarn(t, guestAllergens);
        return (
          <span key={t} className={`fnb-allergen${warn ? ' fnb-allergen--warn' : ''}`}>
            {warn ? '⚠ ' : ''}
            {allergenLabel(t)}
          </span>
        );
      })}
    </span>
  );
}

/** Одна row-card позиции меню (§11). */
function MenuRow({
  category,
  item,
  locale,
  cart,
  guestAllergens,
}: {
  category: CatalogViewCategory;
  item: CatalogViewItem;
  locale: string;
  cart: ReturnType<typeof useCart>;
  guestAllergens: readonly string[];
}): JSX.Element {
  const hasVariants = item.variants.length > 0;
  const price = itemUnitPrice(item, locale);

  return (
    <li className="fnb-row" data-testid={`menu-item-${item.code}`}>
      <div className="fnb-row__main">
        {item.image_url !== undefined && item.image_url !== '' ? (
          <img
            className="fnb-row__photo"
            src={item.image_url}
            alt={item.name}
            loading="lazy"
            data-testid={`menu-item-${item.code}-photo`}
          />
        ) : (
          <span className="fnb-row__photo fnb-row__photo--empty" aria-hidden="true">
            🍽️
          </span>
        )}
        <div className="fnb-row__text">
          <span className="fnb-row__name">{item.name}</span>
          {item.description !== undefined && item.description !== '' && (
            <span className="fnb-row__desc">{item.description}</span>
          )}
          <AllergenChips item={item} guestAllergens={guestAllergens} />
        </div>
        {!hasVariants && (
          <div className="fnb-row__right">
            {price !== null && <span className="fnb-row__price">{price}</span>}
            <QtyStepper
              qty={cart.qtyOf(item.id)}
              testid={`menu-item-${item.code}`}
              onAdd={() => cart.add({ category, item })}
              onInc={() => cart.add({ category, item })}
              onDec={() => cart.setQty(item.id, cart.qtyOf(item.id) - 1)}
            />
          </div>
        )}
      </div>

      {hasVariants && (
        <ul className="fnb-variants">
          {item.variants.map((v) => {
            const qty = cart.qtyOf(item.id, v.id);
            return (
              <li className="fnb-variant" key={v.id} data-testid={`menu-variant-${v.id}`}>
                <span className="fnb-variant__name">{v.name}</span>
                {item.show_price && (
                  <span className="fnb-variant__price">{displayVariantPrice(v, locale)}</span>
                )}
                <QtyStepper
                  qty={qty}
                  testid={`menu-variant-${v.id}`}
                  onAdd={() => cart.add({ category, item, variant: v })}
                  onInc={() => cart.add({ category, item, variant: v })}
                  onDec={() => cart.setQty(`${item.id}:${v.id}`, qty - 1)}
                />
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

export function GuestMenuCategory(): JSX.Element {
  const locale = useMemo(() => resolveGuestLocale(), []);
  const navigate = useNavigate();
  const { categoryCode } = useParams<{ categoryCode: string }>();
  const { data: categories, isLoading, isError, refetch } = useFnbCatalog(locale);
  const cart = useCart();
  // Предпочтения гостя по аллергенам (профиля-API нет — пусто; warning по
  // умолчанию подсветит потенциально опасные теги из ALLERGEN_WARN).
  const guestAllergens: readonly string[] = [];

  const category = categories?.find((c) => c.code === categoryCode);

  function back(): void {
    navigate('/menu');
  }

  if (isLoading) {
    return (
      <section aria-busy="true" data-testid="menu-category-skeleton">
        <button type="button" className="fnb-back" onClick={back} data-testid="menu-back">
          ‹ Меню
        </button>
        <ul className="fnb-rows">
          {Array.from({ length: 5 }, (_, i) => (
            <li key={i} className="fnb-row fnb-row--skeleton" data-testid="menu-skeleton-row">
              <Skeleton height={60} width={60} radius={12} />
              <div style={{ flex: 1, marginLeft: 12 }}>
                <Skeleton height={16} radius={6} />
                <div style={{ height: 8 }} />
                <Skeleton width={140} height={12} radius={6} />
              </div>
            </li>
          ))}
        </ul>
      </section>
    );
  }

  if (isError || categories === undefined) {
    return (
      <section data-testid="menu-category-error">
        <button type="button" className="fnb-back" onClick={back} data-testid="menu-back">
          ‹ Меню
        </button>
        <Card>
          <p role="alert">Не удалось загрузить меню. Попробовать ещё раз?</p>
          <div className="rq-actions">
            <Button variant="secondary" onClick={() => void refetch()}>
              Попробовать ещё раз
            </Button>
          </div>
        </Card>
      </section>
    );
  }

  if (category === undefined) {
    return (
      <section data-testid="menu-category-missing">
        <button type="button" className="fnb-back" onClick={back} data-testid="menu-back">
          ‹ Меню
        </button>
        <Card>
          <p>Сейчас в этой категории ничего нет. Загляните в другие разделы меню →</p>
          <div className="rq-actions">
            <Button variant="secondary" onClick={back}>
              К категориям меню
            </Button>
          </div>
        </Card>
        <GuestCartCta locale={locale} cart={cart} />
      </section>
    );
  }

  return (
    <section data-testid="menu-category-page">
      <div className="fnb-cat-head">
        <button type="button" className="fnb-back" onClick={back} data-testid="menu-back">
          ‹ Меню
        </button>
        <h1 className="hsc-page__title">{category.name}</h1>
      </div>

      {category.items.length === 0 ? (
        <Card data-testid="menu-category-empty">
          <p>Сейчас в этой категории ничего нет. Загляните в другие разделы меню →</p>
          <div className="rq-actions">
            <Button variant="secondary" onClick={back}>
              К категориям меню
            </Button>
          </div>
        </Card>
      ) : (
        <ul className="fnb-rows" data-testid="menu-rows">
          {category.items.map((item) => (
            <MenuRow
              key={item.id}
              category={category}
              item={item}
              locale={locale}
              cart={cart}
              guestAllergens={guestAllergens}
            />
          ))}
        </ul>
      )}

      <GuestCartCta locale={locale} cart={cart} />
    </section>
  );
}
